import { useRef, useEffect } from "react";

import { useAppSelector } from "app/hooks";
import { DateTime } from "luxon";
import { selectApplicationInsights } from "features/config/store/selectors";
import {
    ApplicationInsights,
    IDependencyInitializerHandler,
} from "@microsoft/applicationinsights-web";
import { ITelemetryInitializerHandler } from "@microsoft/applicationinsights-core-js";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { createBrowserHistory } from "history";
import { appFeaturesSelectors } from "features/appFeatures/store/selectors";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { TelemetryRuleIgnoreMode } from "types/TelemetryRuleIgnoreMode";

export const useApplicationInsights = () => {
    const appInsightsConfiguration = useAppSelector(selectApplicationInsights);
    const selectHasSuccessfullyFetchedFeatures = useAppSelector((state) =>
        appFeaturesSelectors.selectHasSuccessfullyFetchedFeatures(state),
    );
    const diagnosticFeatureEnabled = useAppSelector((state) =>
        appFeaturesSelectors.selectUserHasFeatures(state, [
            AppFeature.Diagnostics,
        ]),
    );

    const reactPlugin = useRef<ReactPlugin>(new ReactPlugin());
    const applicationInsightsInstance = useRef<ApplicationInsights>(undefined);
    const dependencyInitializerHandler =
        useRef<IDependencyInitializerHandler>(undefined);
    const filterTelemetryInitializerHandler =
        useRef<ITelemetryInitializerHandler>(undefined);

    useEffect(() => {
        if (
            appInsightsConfiguration?.connectionString &&
            !applicationInsightsInstance.current
        ) {
            const browserHistory = createBrowserHistory();

            applicationInsightsInstance.current = new ApplicationInsights({
                config: {
                    connectionString: appInsightsConfiguration.connectionString,
                    disableInstrumentationKeyValidation: true, // we use a dummy value which is replaced server-side
                    extensions: [reactPlugin.current],
                    extensionConfig: {
                        [reactPlugin.current.identifier]: {
                            history: browserHistory,
                        },
                        ["AppInsightsCfgSyncPlugin"]: {
                            cfgUrl: "",
                        },
                    },
                    // With enableAutoRouteTracking enabled, a new operation ID is generated on every route change (Also on (de)selection of devices)
                    enableAutoRouteTracking: true,
                    maxAjaxCallsPerView:
                        appInsightsConfiguration.maxAjaxCallsPerView,
                    enableCorsCorrelation: true,
                    enableRequestHeaderTracking: true,
                },
            });

            // Provide a Cloud Role Name for all browser telemetry
            applicationInsightsInstance.current.addTelemetryInitializer(
                (envelope) => {
                    envelope.tags = envelope.tags || {};
                    envelope.tags["ai.cloud.role"] = "Front-End"; // Cloud Role Name
                },
            );

            // Provide a Dependency Initializer to filter out specific stream packages and enrich the dependency telemetry with meta data
            if (appInsightsConfiguration.streamPackageDependencyFilter) {
                if (dependencyInitializerHandler.current) {
                    dependencyInitializerHandler.current.remove();
                }

                dependencyInitializerHandler.current =
                    applicationInsightsInstance.current.addDependencyInitializer(
                        (details) => {
                            if (details.item.target) {
                                const lowerCaseTarget =
                                    details.item.target.toLowerCase();

                                if (
                                    lowerCaseTarget.indexOf(
                                        appInsightsConfiguration
                                            .streamPackageDependencyFilter
                                            .streamRequestUrlStartsWith,
                                    ) > -1
                                ) {
                                    if (
                                        streamTelemetryFilter(
                                            lowerCaseTarget,
                                            appInsightsConfiguration
                                                .streamPackageDependencyFilter
                                                .streamRequestUrlFilterRegex,
                                        )
                                    ) {
                                        return false;
                                    }
                                }
                            }

                            // Also capture the start and end time of the dependency requests
                            if (
                                details.item.startTime &&
                                details.item.duration
                            ) {
                                const startTime = DateTime.fromJSDate(
                                    details.item.startTime,
                                );
                                const endTime = startTime.plus({
                                    milliseconds: details.item.duration,
                                });

                                details.item.properties = {
                                    ...details.item.properties,
                                    StartTime: startTime.toISO(),
                                    EndTime: endTime.toISO(),
                                };
                            }
                        },
                    );
            }

            applicationInsightsInstance.current.loadAppInsights();
        }
    }, [appInsightsConfiguration]);

    // Register general telemetry filter using ignore rules to specify the types of telemetry that will not be sent to Azure
    useEffect(() => {
        if (
            applicationInsightsInstance.current &&
            appInsightsConfiguration?.telemetryIgnoreRules &&
            appInsightsConfiguration.telemetryIgnoreRules.length > 0 &&
            selectHasSuccessfullyFetchedFeatures
        ) {
            if (filterTelemetryInitializerHandler.current) {
                filterTelemetryInitializerHandler.current.remove();
            }

            filterTelemetryInitializerHandler.current =
                applicationInsightsInstance.current.addTelemetryInitializer(
                    (item) => {
                        const isSuccess = !!item.baseData?.success;
                        const lowerCaseTarget: string = (
                            item.baseData?.target ??
                            item.baseData?.url ??
                            item.baseData?.name ??
                            "unknown"
                        ).toLowerCase();

                        const ignoreRuleMatched =
                            appInsightsConfiguration.telemetryIgnoreRules
                                // evaluate which rule(s) match the current telemetry type
                                .filter((x) =>
                                    x.types.includes(
                                        item.baseType ?? "unknown",
                                    ),
                                )
                                // apply rules based on their ignore mode
                                .filter((x) =>
                                    evaluateIgnoreMode(
                                        x.ignoreMode,
                                        diagnosticFeatureEnabled,
                                        isSuccess,
                                    ),
                                )
                                // any match on target? (rules without a specified target will always match)
                                .some(
                                    (x) =>
                                        x.targetStartsWith === undefined ||
                                        lowerCaseTarget.indexOf(
                                            x.targetStartsWith,
                                        ) > -1,
                                );

                        if (ignoreRuleMatched) {
                            // Stop telemetry from being reported
                            return false;
                        }
                    },
                );
        }
    }, [
        appInsightsConfiguration,
        applicationInsightsInstance.current,
        selectHasSuccessfullyFetchedFeatures,
        diagnosticFeatureEnabled,
    ]);

    return {
        reactPlugin: reactPlugin.current,
        applicationInsightsInstance: applicationInsightsInstance.current,
    };
};

function streamTelemetryFilter(
    requestUrl: string,
    regExpression: string,
): boolean {
    const streamEndpointVideoPartRegex = RegExp(regExpression);
    const match = streamEndpointVideoPartRegex.exec(requestUrl);

    // Only allow the initial index file, the init and every 100th streaming package to be tracked
    if (match?.groups) {
        if (
            match.groups.segmentType === "init" ||
            match.groups.playlistType === "index"
        ) {
            return false;
        }

        if (match.groups.partNumber) {
            const partNumber = Number(match.groups.partNumber);

            if (partNumber % 100 === 0) {
                return false;
            }
        }

        return true;
    }

    return false;
}

function evaluateIgnoreMode(
    ignoreMode: TelemetryRuleIgnoreMode,
    diagnosticFeatureEnabled: boolean,
    isSuccess: boolean,
): boolean {
    switch (ignoreMode) {
        case TelemetryRuleIgnoreMode.Always:
            return true;
        case TelemetryRuleIgnoreMode.Never:
            return false;
        case TelemetryRuleIgnoreMode.DiagnosticsDisabled:
            return !diagnosticFeatureEnabled;
        case TelemetryRuleIgnoreMode.SuccessOnly:
            return isSuccess;
        default:
            return true;
    }
}
